import map from 'lodash/map';
import find from 'lodash/find';
import unionBy from 'lodash/unionBy';
import filter from 'lodash/filter';
import reject from 'lodash/reject';
import head from 'lodash/head';
import { resolveErrorMessage } from 'modules/errors';
import { Mapper } from 'modules/core';
// Don't change, this resolves circular dependency
import { getInvitationsByAuditionId } from '../auditions-invitations/redux';
import {
  postApplication,
  fetchApplication,
  fetchAllTalentApplications,
  fetchTalentApplications,
  fetchAllProducerApplications,
  fetchProducerApplications,
  fetchAuditionApplications,
  fetchApplicationForTalent,
  postAuditionApplicationUnreadStatus,
  searchApplicationsByName,
  fetchUnreadApplications,
  markAllApplicationMessagesAsRead as apiMarkAllApplicationMessagesAsRead,
} from './api';
import { AuditionApplications } from './services';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

export const loadAllTalentApplications = createAsyncThunk(
  'auditionApplications/loadAllTalent',
  async talentId => await fetchAllTalentApplications(talentId),
);

export const loadTalentApplications = createAsyncThunk(
  'auditionApplications/loadTalent',
  async ({ talentId, sortBy, limit }) =>
    await fetchTalentApplications(talentId, sortBy, limit),
);

export const loadAllProducerApplications = createAsyncThunk(
  'auditionApplications/loadAllProducer',
  async producerId => fetchAllProducerApplications(producerId),
);

export const loadProducerApplications = createAsyncThunk(
  'auditionApplications/loadProducer',
  async ({ producerId, sortBy, limit }) =>
    await fetchProducerApplications(producerId, sortBy, limit),
);

export const loadAuditionApplications = createAsyncThunk(
  'auditionApplications/loadAll',
  async auditionId => await fetchAuditionApplications(auditionId),
);

export const loadApplicationForTalent = createAsyncThunk(
  'auditionApplications/loadApplication',
  async ({ auditionId, talentId }) =>
    await fetchApplicationForTalent(auditionId, talentId),
);

//TODO: is this even used?
export const loadApplication = createAsyncThunk(
  'applications/load',
  async ({ talentId, auditionId }) =>
    await fetchApplication(talentId, auditionId),
);

export const createApplication = createAsyncThunk(
  'applications/create',
  async ({ talentId, auditionId }) =>
    await postApplication(talentId, auditionId),
);

export const setAuditionApplicationUnreadStatus = createAsyncThunk(
  'applications/markUnread',
  async ({ auditionId, applicationId, unreadMessageForAccountId }) =>
    await postAuditionApplicationUnreadStatus(
      auditionId,
      applicationId,
      unreadMessageForAccountId,
    ),
);

export const searchApplications = createAsyncThunk(
  'applications/search',
  async ({ query, includeAccount, shouldSearchUnread, accountId }) =>
    await searchApplicationsByName(
      query,
      includeAccount,
      shouldSearchUnread,
      accountId,
    ),
);

export const loadUnreadApplications = createAsyncThunk(
  'applications/loadUnread',
  async ({ accountId, accountTypeId, accountType, unreadMessageCount }) =>
    await fetchUnreadApplications(
      accountId,
      accountTypeId,
      accountType,
      unreadMessageCount,
    ),
);

export const markAllApplicationMessagesAsRead = createAsyncThunk(
  'applications/markAllasRead',
  async _ => await apiMarkAllApplicationMessagesAsRead(),
);

const auditionApplicationsSlice = createSlice({
  name: 'auditionApplications',
  initialState: {
    applications: [],
    searchedApplications: [],
    loading: false,
    submitError: null,
    applicationsLoading: false,
    applicationLoading: false,
    auditionMessagesLoading: false,
    auditioneesSort: '-createdAt',
  },
  reducers: {
    setAuditioneesSort: (state, action) => {
      state.auditioneesSort = action.payload;
    },
    clearSearchedApplications: (state, _) => {
      state.searchedApplications = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadAllTalentApplications.pending, (state, _) => {
        state.applicationsLoading = true;
      })
      .addCase(loadAllTalentApplications.fulfilled, (state, action) => {
        const newApplications = action.payload.data;

        state.applicationsLoading = false;
        state.applications = unionBy(newApplications, state.applications, 'id');
      })
      .addCase(loadAllTalentApplications.rejected, (state, _) => {
        state.applicationsLoading = false;
      })
      .addCase(loadTalentApplications.pending, (state, _) => {
        state.auditionMessagesLoading = true;
      })
      .addCase(loadTalentApplications.fulfilled, (state, action) => {
        const newApplications = action.payload?.data;

        state.auditionMessagesLoading = false;
        state.applications = unionBy(newApplications, state.applications, 'id');
      })
      .addCase(loadTalentApplications.rejected, (state, _) => {
        state.auditionMessagesLoading = false;
      })
      .addCase(loadAllProducerApplications.pending, (state, _) => {
        state.applicationsLoading = true;
      })
      .addCase(loadAllProducerApplications.fulfilled, (state, action) => {
        const newApplications = action.payload.data;

        state.applicationsLoading = false;
        state.applications = unionBy(newApplications, state.applications, 'id');
      })
      .addCase(loadAllProducerApplications.rejected, (state, _) => {
        state.applicationsLoading = false;
      })
      .addCase(loadProducerApplications.pending, (state, _) => {
        state.applicationsLoading = true;
      })
      .addCase(loadProducerApplications.fulfilled, (state, action) => {
        const newApplications = action.payload.data;

        state.applicationsLoading = false;
        state.applications = unionBy(newApplications, state.applications, 'id');
      })
      .addCase(loadProducerApplications.rejected, (state, _) => {
        state.applicationsLoading = false;
      })
      .addCase(loadApplication.fulfilled, (state, action) => {
        const application = action.payload.data;
        state.applications = Mapper.addOrReplace(
          state.applications,
          application,
        );
      })
      .addCase(createApplication.fulfilled, (state, action) => {
        const newApplication = action.payload.data;

        state.applications = [...state.applications, newApplication];
        state.submitError = false;
      })
      .addCase(createApplication.rejected, (state, action) => {
        state.submitError = resolveErrorMessage(action.error);
      })
      .addCase(loadAuditionApplications.pending, (state, _) => {
        state.applicationLoading = true;
      })
      .addCase(loadAuditionApplications.fulfilled, (state, action) => {
        const newApplications = action.payload.data;

        state.applications = unionBy(newApplications, state.applications, 'id');
        state.applicationsLoading = false;
      })
      .addCase(loadApplicationForTalent.pending, (state, _) => {
        state.applicationLoading = true;
      })
      .addCase(loadApplicationForTalent.fulfilled, (state, action) => {
        const newApplication = action.payload.data;

        state.applicationLoading = false;
        state.applications = Mapper.addOrReplace(
          state.applications,
          newApplication,
        );
      })
      .addCase(loadApplicationForTalent.rejected, (state, _) => {
        state.applicationLoading = false;
      })
      .addCase(searchApplications.pending, (state, _) => {
        state.applicationsLoading = true;
      })
      .addCase(searchApplications.fulfilled, (state, action) => {
        state.applicationsLoading = false;
        state.searchedApplications = action.payload.data;
      })
      .addCase(searchApplications.rejected, (state, _) => {
        state.applicationsLoading = false;
      })
      .addCase(loadUnreadApplications.pending, (state, _) => {
        state.applicationsLoading = true;
      })
      .addCase(loadUnreadApplications.fulfilled, (state, action) => {
        state.applicationsLoading = false;
        state.searchedApplications = action.payload.data;
      })
      .addCase(loadUnreadApplications.rejected, (state, _) => {
        state.applicationsLoading = false;
      })
      .addCase(markAllApplicationMessagesAsRead.pending, (state, _) => {
        state.applicationsLoading = true;
      })
      .addCase(markAllApplicationMessagesAsRead.fulfilled, (state, _) => {
        state.applicationsLoading = false;
      })
      .addCase(markAllApplicationMessagesAsRead.rejected, (state, _) => {
        state.applicationsLoading = false;
      });
  },
  selectors: {
    getApplications: state => state.applications,
    getApplicationsLoading: state => state.applicationsLoading,
    getApplicationLoading: state => state.applicationLoading,
    getAuditionMessagesLoading: state => state.auditionMessagesLoading,
    getAuditioneesSort: state => state.auditioneesSort,
    getSearchedApplications: state => state.searchedApplications,
  },
});

export const {
  setAuditioneesSort,
  clearSearchedApplications,
} = auditionApplicationsSlice.actions;
export const reducer = auditionApplicationsSlice.reducer;
export const {
  getApplications,
  getApplicationsLoading,
  getApplicationLoading,
  getAuditionMessagesLoading,
  getAuditioneesSort,
  getSearchedApplications,
} = auditionApplicationsSlice.selectors;

export const getApplicationByTalentAndAuditionId = (talentId, auditionId) =>
  createSelector(
    getApplications,
    stateApplications =>
      find(
        stateApplications,
        application =>
          application.auditionId === auditionId &&
          application.talentId === talentId,
      ),
  );

export const getApplicationsByAuditionId = auditionId =>
  createSelector(
    getApplications,
    stateApplications =>
      filter(
        stateApplications,
        application => application.auditionId === auditionId,
      ),
  );

export const getApplicationById = applicationId =>
  createSelector(
    getApplications,
    stateApplications =>
      find(stateApplications, application => application.id === applicationId),
  );

export const getAuditionees = auditionId =>
  createSelector(
    getInvitationsByAuditionId(auditionId),
    getApplicationsByAuditionId(auditionId),
    (invitations, applications) => {
      const invitationsWithoutSubmission = reject(
        invitations,
        invitation => !!find(applications, { talentId: invitation.talentId }),
      );
      const talentsWithoutSubmission = map(
        invitationsWithoutSubmission,
        invitation => {
          return { ...invitation.talent, source: 'Invited' };
        },
      );
      const applicants = map(applications, application => {
        return { ...application.talent, source: 'Applied' };
      });

      return [...applicants, ...talentsWithoutSubmission];
    },
  );

export const getApplicationsSortedByLatestMessages = createSelector(
  getApplications,
  applications => {
    return AuditionApplications.applySortOptions(
      applications,
      undefined,
      AuditionApplications.SORT_OPTIONS.UPDATED_AT,
      'desc',
    );
  },
);

export const getLatestAuditionApplication = createSelector(
  getApplicationsSortedByLatestMessages,
  head,
);
